import React from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  DEFAULT_HOST_TRAINING,
  type HostTrainingContent,
} from '../../../shared/homepage/searchFirstDefaults';
import { HOMEPAGE_SECTION_PY } from '../../../shared/homepage/homepageLayout';
import { trackEvent } from '../../utils/analytics';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';

export type HostTrainingSectionProps = {
  content?: HostTrainingContent | null;
};

const HostTrainingSection: React.FC<HostTrainingSectionProps> = ({ content }) => {
  const data = content || DEFAULT_HOST_TRAINING;

  const track = (cta: 'primary' | 'secondary') => {
    trackEvent({
      action: 'provider_acquisition_inline_cta_click',
      category: 'provider_acquisition',
      label: `homepage_host_section_${cta}`,
      custom_parameters: { source: 'homepage_host_section', cta },
    });
  };

  return (
    <Box
      sx={{
        py: HOMEPAGE_SECTION_PY.host,
        bgcolor: 'background.paper',
        px: AGENCY_SHELL_HORIZONTAL_PX,
      }}
    >
      <Container maxWidth="md" disableGutters>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            mb: 1.5,
            textAlign: 'center',
            letterSpacing: '-0.02em',
          }}
        >
          {data.title}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 4, maxWidth: 640, mx: 'auto', lineHeight: 1.65 }}
        >
          {data.subtitle}
        </Typography>

        <Stack spacing={0} sx={{ maxWidth: 420, mx: 'auto', mb: 4 }}>
          {data.bullets.map((bullet, index) => (
            <Box
              key={bullet}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                position: 'relative',
                pb: index < data.bullets.length - 1 ? 2.5 : 0,
                '&::before':
                  index < data.bullets.length - 1
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 15,
                        top: 32,
                        bottom: 0,
                        width: 2,
                        bgcolor: 'divider',
                      }
                    : undefined,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  flexShrink: 0,
                  zIndex: 1,
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, pt: 0.5, lineHeight: 1.5 }}>
                {bullet}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Typography
          variant="subtitle1"
          sx={{ textAlign: 'center', fontWeight: 700, color: 'primary.main', mb: 3 }}
        >
          {data.freeNote}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            component={RouterLink}
            to={data.primaryCtaHref}
            variant="contained"
            size="large"
            onClick={() => track('primary')}
            sx={{ fontWeight: 700, px: 4, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
          >
            {data.primaryCtaLabel}
          </Button>
          <Button
            component={RouterLink}
            to={data.secondaryCtaHref}
            variant="outlined"
            size="large"
            onClick={() => track('secondary')}
            sx={{ fontWeight: 600 }}
          >
            {data.secondaryCtaLabel}
          </Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default HostTrainingSection;
